"use client";

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { DefaultPageLayout } from '../ui/layouts/DefaultPageLayout';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { useAuthStore } from '../store/authStore';
import {
  getMyProjects,
  getMyCollections,
  updateCollectionProject,
  type ManagedProject,
  type CreatorCollection,
} from '../api/creator';
import { FeatherLock, FeatherUsers, FeatherArrowRight, FeatherSettings, FeatherImage, FeatherFolderPlus } from '@subframe/core';

export const CreatorDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthStore();

  // State
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState<ManagedProject[]>([]);
  const [collections, setCollections] = useState<CreatorCollection[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Load data on mount
  const loadData = useCallback(async () => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const [projectData, collectionData] = await Promise.all([
        getMyProjects(),
        getMyCollections(),
      ]);
      setProjects(projectData);
      setCollections(collectionData);
    } catch (err) {
      console.error('Error loading creator dashboard:', err);
      setError('Failed to load your projects. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // "Add to project" state
  const [assigningCollectionId, setAssigningCollectionId] = useState<string | null>(null);

  const handleAssignToProject = async (collectionId: string, projectId: string) => {
    try {
      await updateCollectionProject(collectionId, projectId);
      // Remove from orphaned list
      setCollections((prev) => prev.filter((c) => c.id !== collectionId));
      setAssigningCollectionId(null);
    } catch (err) {
      console.error('Failed to assign collection to project:', err);
    }
  };

  // Navigate to project management
  const handleManageProject = (slug: string) => {
    navigate(`/creator/projects/${slug}`);
  };

  // Not authenticated
  if (!isAuthenticated) {
    return (
      <DefaultPageLayout>
        <div className="flex w-full flex-col items-center justify-center py-16 gap-4">
          <FeatherLock className="h-12 w-12 text-neutral-400" />
          <h2 className="text-heading-3 font-heading-3 text-default-font">Sign In Required</h2>
          <p className="text-body font-body text-subtext-color text-center max-w-md">
            Please sign in with Xaman to access your creator dashboard and manage your projects.
          </p>
        </div>
      </DefaultPageLayout>
    );
  }

  // Loading state
  if (loading) {
    return (
      <DefaultPageLayout>
        <div className="flex w-full flex-col items-center justify-center py-16">
          <LoadingSpinner size="lg" />
          <p className="mt-4 text-body font-body text-subtext-color">Loading your projects...</p>
        </div>
      </DefaultPageLayout>
    );
  }

  // Error state
  if (error) {
    return (
      <DefaultPageLayout>
        <div className="flex w-full flex-col items-center justify-center py-16 gap-4">
          <p className="text-body font-body text-error-600">{error}</p>
          <button
            onClick={loadData}
            className="px-4 py-2 rounded-md bg-brand-600 text-white hover:bg-brand-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </DefaultPageLayout>
    );
  }

  return (
    <DefaultPageLayout>
      <div className="flex w-full flex-col items-start gap-6">
        {/* Header */}
        <div className="flex flex-col gap-2 w-full">
          <h1 className="text-heading-2 font-heading-2 text-default-font">Creator Dashboard</h1>
          <p className="text-body font-body text-subtext-color">
            Manage your projects, collections, team members, and settings.
          </p>
        </div>

        {/* Projects List */}
        <div className="w-full">
          <h2 className="text-heading-3 font-heading-3 text-default-font mb-4">
            My Projects
          </h2>

          {projects.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 gap-4 border border-dashed border-neutral-border rounded-lg">
              <FeatherUsers className="h-12 w-12 text-neutral-400" />
              <p className="text-body font-body text-subtext-color text-center">
                You don't have any projects yet.
              </p>
              <p className="text-caption font-caption text-subtext-color text-center">
                Projects you own or are invited to manage will appear here.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4">
              {projects.map((project) => (
                <div
                  key={project.id}
                  className="flex items-center gap-4 p-4 rounded-lg border border-neutral-border bg-default-background hover:border-brand-300 transition-colors cursor-pointer"
                  onClick={() => handleManageProject(project.slug)}
                >
                  {/* Project Image */}
                  <div className="flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden bg-neutral-200">
                    {project.image_url ? (
                      <img
                        src={project.image_url}
                        alt={project.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-neutral-400">
                        <FeatherUsers className="h-6 w-6" />
                      </div>
                    )}
                  </div>

                  {/* Project Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="text-body-bold font-body-bold text-default-font truncate">
                        {project.name}
                      </h3>
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          project.role === 'owner'
                            ? 'bg-brand-100 text-brand-700'
                            : 'bg-blue-100 text-blue-700'
                        }`}
                      >
                        {project.role === 'owner' ? 'Owner' : 'Manager'}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 mt-1 text-caption font-caption text-subtext-color">
                      <span>{project.collection_count} collection{project.collection_count !== 1 ? 's' : ''}</span>
                      {project.description && (
                        <span className="truncate">{project.description}</span>
                      )}
                    </div>
                  </div>

                  {/* Manage Button */}
                  <button
                    className="flex items-center gap-2 px-3 py-2 rounded-md bg-neutral-100 text-neutral-700 hover:bg-neutral-200 transition-colors"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleManageProject(project.slug);
                    }}
                  >
                    <FeatherSettings className="h-4 w-4" />
                    <span className="hidden sm:inline">Manage</span>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
        {/* Orphaned Collections List */}
        {collections.length > 0 && (
          <div className="w-full">
            <h2 className="text-heading-3 font-heading-3 text-default-font mb-1">
              My Collections
            </h2>
            <p className="text-caption font-caption text-subtext-color mb-4">
              Collections you created that aren't part of a project.
            </p>

            <div className="grid grid-cols-1 gap-4">
              {collections.map((collection) => (
                <div
                  key={collection.id}
                  className="flex items-center gap-4 p-4 rounded-lg border border-neutral-border bg-default-background hover:border-brand-300 transition-colors cursor-pointer"
                  onClick={() => navigate(`/creator/collections/${collection.slug}`)}
                >
                  {/* Collection Image */}
                  <div className="flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden bg-neutral-200">
                    {collection.image_url ? (
                      <img
                        src={collection.image_url}
                        alt={collection.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-neutral-400">
                        <FeatherImage className="h-6 w-6" />
                      </div>
                    )}
                  </div>

                  {/* Collection Info */}
                  <div className="flex-1 min-w-0">
                    <h3 className="text-body-bold font-body-bold text-default-font truncate">
                      {collection.name}
                    </h3>
                    <div className="flex items-center gap-4 mt-1 text-caption font-caption text-subtext-color">
                      <span>{collection.total_supply} NFTs</span>
                      {collection.floor_price != null && collection.floor_price > 0 && (
                        <span>Floor: {(collection.floor_price / 1_000_000).toFixed(2)} XRP</span>
                      )}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex items-center gap-2">
                    {projects.length > 0 && (
                      <div className="relative">
                        <button
                          className="flex items-center gap-1 px-2 py-2 rounded-md text-neutral-500 hover:bg-neutral-100 transition-colors"
                          title="Add to project"
                          onClick={(e) => {
                            e.stopPropagation();
                            setAssigningCollectionId(
                              assigningCollectionId === collection.id ? null : collection.id
                            );
                          }}
                        >
                          <FeatherFolderPlus className="h-4 w-4" />
                        </button>
                        {assigningCollectionId === collection.id && (
                          <div className="absolute right-0 top-full mt-1 w-48 bg-default-background border border-neutral-border rounded-lg shadow-lg z-10">
                            <div className="p-2">
                              <p className="text-caption font-caption text-subtext-color mb-2 px-2">Add to project:</p>
                              {projects.map((p) => (
                                <button
                                  key={p.id}
                                  className="w-full text-left px-2 py-1.5 rounded text-body text-default-font hover:bg-neutral-100 transition-colors truncate"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleAssignToProject(collection.id, p.id);
                                  }}
                                >
                                  {p.name}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    <button
                      className="flex items-center gap-2 px-3 py-2 rounded-md bg-neutral-100 text-neutral-700 hover:bg-neutral-200 transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/creator/collections/${collection.slug}`);
                      }}
                    >
                      <FeatherSettings className="h-4 w-4" />
                      <span className="hidden sm:inline">Manage</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </DefaultPageLayout>
  );
};
